import { useEffect, useRef } from 'react';
import { isNil } from 'lodash';
import { useForm, useWatch } from 'react-hook-form';

import { useDrawer } from '#apps/client-web/app/hooks/use-drawer';
import { useLogger } from '#apps/client-web/app/hooks/use-logger';

import { useUserSettings } from '../../hooks/use-user-settings';
import type { UserSettings } from '../../models/user-settings';

export const useUserSettingsPageViewModel = () => {
  const logger = useLogger(
    'features/user-settings/pages/user-settings/view-model',
  );

  const initializedRef = useRef(false);

  const drawer = useDrawer();
  const { getUserSettingsQuery, updateUserSettingsMutation } =
    useUserSettings();

  const form = useForm<UserSettings>({
    defaultValues: getUserSettingsQuery.data,
  });

  // 1) Initialize/reset form when data first becomes available
  useEffect(() => {
    // Skip if no data
    if (isNil(getUserSettingsQuery.data)) {
      return;
    }

    // Prevent resetting form if already initialized
    if (initializedRef.current) {
      return;
    }

    // Initialize form with fetched user settings
    logger.debug(
      { userSettings: getUserSettingsQuery.data },
      'Initializing form with fetched user settings',
    );
    form.reset(getUserSettingsQuery.data);
    initializedRef.current = true;
  }, [getUserSettingsQuery.data, initializedRef, form.reset, logger]);

  // 2) Auto-save form changes (watch + debounce)

  // Watch all form fields
  const watchedSettings = useWatch({
    control: form.control,
  });

  // Auto-save effect
  useEffect(() => {
    // don't run before initial reset
    if (!initializedRef.current) {
      return;
    }

    // only when user changed something
    if (!form.formState.isDirty || !form.formState.isValid) {
      return;
    }

    const timeout = setTimeout(() => {
      logger.debug(
        { userSettings: watchedSettings },
        'Auto-saving changed user settings',
      );

      updateUserSettingsMutation.mutate(watchedSettings as UserSettings);

      form.reset(watchedSettings as UserSettings, { keepDirty: false });
    }, 150); // 150ms debounce

    return () => {
      clearTimeout(timeout);
    };
  }, [
    initializedRef,
    form.formState.isDirty,
    form.formState.isValid,
    watchedSettings,
    form.reset,
    updateUserSettingsMutation.mutate,
  ]);

  return {
    drawer,
    form,
    isReady: initializedRef.current === true && getUserSettingsQuery.isSuccess,
  };
};

export type UserSettingsPageViewModel = ReturnType<
  typeof useUserSettingsPageViewModel
>;
